import React from "react";
import { Box, Button, Chip, IconButton, Paper, TextField, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { enqueueSnackbar } from "notistack";
import { DataTable, FormDialog, SelectField } from "../../Layout/Common";
import { FIXED_TABLE } from "./loc";

// What the AI reads: the languages and their local names, the base language, the free-text project context, the
// glossary (term, meaning, and the fixed translation of the term in each language) and the model. Saved whole
// through `context set`. A term's fixed translations are edited in a dialog with one field per language, never as
// typed language codes.
export default class ContextTab extends React.Component {
    state = { draft: null, dirty: false, saving: false, newCode: "", newName: "", fixedFor: null };   // fixedFor: the glossary row being edited

    componentDidMount() { this.reset(); }
    reload() { if (!this.state.dirty) this.reset(); }

    reset = () => {
        const p = this.props.project;
        this.setState({ dirty: false, draft: {
            baseLanguage: p.baseLanguage, model: p.model, context: p.context ?? "",
            languages: (p.languages ?? []).map(l => ({ code: l.code, localName: l.localName ?? "" })),
            glossary: (p.glossary ?? []).map(g => ({ term: g.term ?? "", meaning: g.meaning ?? "", fixedTexts: { ...(g.fixedTexts ?? {}) } })),
        } });
    }

    set = (patch, after) => this.setState(s => ({ draft: { ...s.draft, ...patch }, dirty: true }), after);
    setLang = (i, patch) => this.set({ languages: this.state.draft.languages.map((l, j) => j === i ? { ...l, ...patch } : l) });
    setTerm = (i, patch) => this.set({ glossary: this.state.draft.glossary.map((g, j) => j === i ? { ...g, ...patch } : g) });
    setFixed = (i, code, value) => this.setTerm(i, { fixedTexts: { ...(this.state.draft.glossary[i].fixedTexts ?? {}), [code]: value } });

    // One field per language for the term's fixed translation: what the AI must use instead of translating the term
    renderFixedDialog() {
        const { fixedFor, draft } = this.state;
        if (fixedFor == null || !draft.glossary[fixedFor]) return null;
        const g = draft.glossary[fixedFor];
        const targets = draft.languages.filter(l => l.code !== draft.baseLanguage);
        const ro = !this.props.canEdit;
        return (
            <FormDialog open title={`Fixed translations for “${g.term || "the term"}”`} onClose={() => this.setState({ fixedFor: null })}
                        onConfirm={() => this.setState({ fixedFor: null })} confirmText="Done" cancelText="Close" hideConfirm={ro} fullWidth maxWidth="sm">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    The AI uses these exactly as written and never translates the term itself. Leave a language empty to let the AI choose.
                </Typography>
                <TextField label={`${draft.baseLanguage} (the term)`} value={g.term} size="small" fullWidth sx={{ mb: 2 }} disabled />
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {targets.map(l => (
                        <Box key={l.code} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            <Box sx={{ width: 130, flexShrink: 0 }}>
                                <Typography variant="body2"><b>{l.code}</b></Typography>
                                <Typography variant="caption" color="text.secondary">{l.localName}</Typography>
                            </Box>
                            <TextField value={g.fixedTexts?.[l.code] ?? ""} onChange={e => this.setFixed(fixedFor, l.code, e.target.value)}
                                       size="small" fullWidth disabled={ro} placeholder={`“${g.term}” in ${l.localName || l.code}`} />
                        </Box>
                    ))}
                </Box>
            </FormDialog>
        );
    }

    addLang = () => {
        const code = this.state.newCode.trim().toUpperCase();
        if (!/^[A-Z]{2,3}$/.test(code) || this.state.draft.languages.some(l => l.code === code)) return;
        this.set({ languages: [...this.state.draft.languages, { code, localName: this.state.newName.trim() }] });
        this.setState({ newCode: "", newName: "" });
    }

    save = () => {
        const p = this.props.project;
        const d = this.state.draft;
        const payload = {
            titleId: p.titleId, baseLanguage: d.baseLanguage.trim().toUpperCase(), model: d.model, context: d.context,
            languages: d.languages.map(l => ({ code: l.code, localName: l.localName })),
            glossary: d.glossary.filter(g => g.term.trim()).map(g => ({ term: g.term.trim(), meaning: g.meaning,
                fixedTexts: Object.fromEntries(Object.entries(g.fixedTexts ?? {}).map(([k, v]) => [k, (v ?? "").trim()]).filter(([, v]) => v)) })),
            tokensPerCharIn: p.tokensPerCharIn, tokensPerCharOut: p.tokensPerCharOut, updatedDate: p.updatedDate, updatedBy: p.updatedBy,
        };
        this.setState({ saving: true });
        this.props.loc("Saving the project context", ["context", "set"], { payload })
            .then(r => { if (typeof r === "string") { enqueueSnackbar(r, { variant: "error" }); return; } this.setState({ dirty: false }); this.props.onProjectChanged(); })
            .catch(() => {})
            .finally(() => this.setState({ saving: false }));
    }

    render() {
        const { project, canEdit } = this.props;
        const { draft, dirty, saving, newCode, newName } = this.state;
        if (!draft) return null;
        const ro = !canEdit;
        const langCols = [
            { id: "code", label: "Code", width: "15%", render: (l) => <b>{l.code}</b> },
            { id: "name", label: "Local name", width: "78%", render: (l, i) => <TextField size="small" fullWidth value={l.localName} onChange={e => this.setLang(i, { localName: e.target.value })} disabled={ro} /> },
            { id: "x", label: "", width: "7%", render: (l, i) => l.code !== draft.baseLanguage && canEdit ? <IconButton size="small" onClick={() => this.set({ languages: draft.languages.filter((_, j) => j !== i) })}><DeleteIcon fontSize="small" /></IconButton> : null },
        ];
        const targets = draft.languages.filter(l => l.code !== draft.baseLanguage);
        const glossCols = [
            { id: "term", label: "Term", width: "19%", sx: { verticalAlign: "top" }, render: (g, i) => <TextField size="small" fullWidth value={g.term} onChange={e => this.setTerm(i, { term: e.target.value })} disabled={ro} /> },
            { id: "meaning", label: "Meaning (what it is in the game)", width: "40%", sx: { verticalAlign: "top" }, render: (g, i) => <TextField size="small" fullWidth multiline minRows={2} maxRows={6} value={g.meaning} onChange={e => this.setTerm(i, { meaning: e.target.value })} disabled={ro} /> },
            { id: "fixed", label: "Fixed translations", width: "37%", sx: { verticalAlign: "top" }, render: (g, i) => {
                const set = targets.filter(l => (g.fixedTexts?.[l.code] ?? "").trim());
                const preview = set.slice(0, 3).map(l => `${l.code} ${g.fixedTexts[l.code]}`).join(" · ");
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Tooltip title={set.length ? set.map(l => `${l.code}: ${g.fixedTexts[l.code]}`).join("\n") : "none set"}>
                            <Chip size="small" color={set.length ? "primary" : "default"} variant={set.length ? "filled" : "outlined"} label={`${set.length} / ${targets.length}`} />
                        </Tooltip>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>{preview || "no language set"}</Typography>
                            <Button size="small" sx={{ px: 0, minWidth: 0 }} onClick={() => this.setState({ fixedFor: i })}>{canEdit ? "Set per language" : "View"}</Button>
                        </Box>
                    </Box>
                );
            }},
            { id: "x", label: "", width: "4%", sx: { verticalAlign: "top" }, render: (g, i) => canEdit ? <IconButton size="small" onClick={() => this.set({ glossary: draft.glossary.filter((_, j) => j !== i) })}><DeleteIcon fontSize="small" /></IconButton> : null },
        ];
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 1100 }}>
                <Paper sx={{ p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                    <SelectField label="Model" size="small" minWidth={220} value={draft.model} options={project.models ?? [draft.model]} onChange={v => this.set({ model: v })} disabled={ro} />
                    <TextField label="Base language" size="small" value={draft.baseLanguage} onChange={e => this.set({ baseLanguage: e.target.value })} sx={{ width: 140 }} disabled={ro} />
                    <Typography variant="caption" color="text.secondary">Estimate rates: {project.tokensPerCharIn} tokens/char in, {project.tokensPerCharOut} out (re-measured by every finished job).</Typography>
                    <Box sx={{ flex: 1 }} />
                    {canEdit && <Button variant="contained" onClick={this.save} disabled={!dirty || saving}>Save</Button>}
                    {canEdit && <Button onClick={this.reset} disabled={!dirty || saving}>Discard</Button>}
                </Paper>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Project context</Typography>
                    <TextField multiline minRows={6} fullWidth value={draft.context} onChange={e => this.set({ context: e.target.value })} disabled={ro}
                               placeholder="Tone, audience, currency and product names, what never gets translated, how to treat placeholders… everything the translator should know about the game." />
                </Paper>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Glossary</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        Terms the translation must never improvise: every request carries this list, and a term with a fixed translation is written exactly as given.
                    </Typography>
                    <DataTable columns={glossCols} rows={draft.glossary} getRowKey={(g, i) => i} sx={FIXED_TABLE} />
                    {canEdit && <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => this.set({ glossary: [...draft.glossary, { term: "", meaning: "", fixedTexts: {} }] }, () => this.setState({ fixedFor: draft.glossary.length }))}>Term</Button>}
                </Paper>
                <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Languages</Typography>
                    <DataTable columns={langCols} rows={draft.languages} getRowKey={l => l.code} sx={FIXED_TABLE} />
                    {canEdit && <Box sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}>
                        <TextField size="small" label="Code" value={newCode} onChange={e => this.setState({ newCode: e.target.value })} sx={{ width: 100 }} />
                        <TextField size="small" label="Local name" value={newName} onChange={e => this.setState({ newName: e.target.value })} />
                        <Button size="small" startIcon={<AddIcon />} onClick={this.addLang}>Language</Button>
                        <Typography variant="caption" color="text.secondary">A new language starts empty: a context translation fills it.</Typography>
                    </Box>}
                </Paper>
                {this.renderFixedDialog()}
            </Box>
        );
    }
}
